import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AxiosService from '../../services/AxiosHelper';
import { toast } from 'react-toastify';

const baseURL = process.env.REACT_APP_BASEURL;

export const getOrders = createAsyncThunk(
    'order/getOrders',
    async (filters, { rejectWithValue }) => {
        try {
            const query = new URLSearchParams(filters).toString();
            const response = await AxiosService.AxiostRequest({
                method: 'get',
                url: `${baseURL}/orders?${query}`
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch orders');
        }
    }
);
export const addOrder = createAsyncThunk(
    'order/addOrder',
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'post',
                url: `${baseURL}/orders`,
                data: values
            });
            toast.success('Order created successfully');
            return response.data.data;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create order');
            return rejectWithValue(err.response?.data?.message || 'Failed to create order');
        }
    }
);

export const getOrderById = createAsyncThunk(
    'order/getOrderById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'get',
                url: `${baseURL}/orders/${id}`
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch order details');
        }
    }
);

export const acceptOrder = createAsyncThunk(
    'order/acceptOrder',
    async (id, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'patch',
                url: `${baseURL}/orders/${id}/accept`
            });
            toast.success('Order accepted');
            return response.data.data;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to accept order');
            return rejectWithValue(err.response?.data?.message || 'Failed to accept order');
        }
    }
);

export const rejectOrder = createAsyncThunk(
    'order/rejectOrder',
    async ({ id, rejectionReason }, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'patch',
                url: `${baseURL}/orders/${id}/reject`,
                data: { rejectionReason }
            });
            toast.success('Order rejected');
            return response.data.data;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reject order');
            return rejectWithValue(err.response?.data?.message || 'Failed to reject order');
        }
    }
);

export const markProcessing = createAsyncThunk(
    'order/markProcessing',
    async (id, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'patch',
                url: `${baseURL}/orders/${id}/processing`
            });
            toast.success('Order status updated to processing');
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to update order status');
        }
    }
);

export const sendPortalInvite = createAsyncThunk(
    'order/sendPortalInvite',
    async (customerId, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: 'post',
                url: `${baseURL}/portal/invite/${customerId}`
            });
            toast.success('Portal invitation sent');
            return response.data.data;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send invite');
            return rejectWithValue(err.response?.data?.message || 'Failed to send invite');
        }
    }
);

const orderSlice = createSlice({
    name: 'order',
    initialState: {
        orders: [],
        currentOrder: null,
        loading: false,
        statusCounts: {},
        pagination: null,
        error: null,
    },
    reducers: {
        clearCurrentOrder: (state) => { state.currentOrder = null; }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOrders.pending, (state) => { state.loading = true; })
            .addCase(getOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.orders = action.payload.list;
                state.statusCounts = action.payload.statusCounts;
                state.pagination = action.payload.pagination;
            })
            .addCase(getOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getOrderById.pending, (state) => { state.loading = true; })
            .addCase(getOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentOrder = action.payload;
            })
            .addCase(getOrderById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(acceptOrder.fulfilled, (state, action) => {
                state.currentOrder = action.payload;
            })
            .addCase(rejectOrder.fulfilled, (state, action) => {
                state.currentOrder = action.payload;
            })
            .addCase(markProcessing.fulfilled, (state, action) => {
                state.currentOrder = action.payload;
            });
    }
});

export const { clearCurrentOrder } = orderSlice.actions;
export default orderSlice.reducer;
